import React from 'react';
import { Dropdown, Form, Grid, Header, Input, Message } from 'semantic-ui-react';

import BankCodeDropdown from '../../../../components/BankCodeDropdown';
import { maskCpf, maskCpfOrCnpj, maskPhoneNumber, maskZipCode } from '../../../../services/masks';

import { ActivationFormState, ActivationStep, CollaboratorActivationFormState, UpdateActivationFormFieldFn } from '../../types';

type Props = {
  mode?: 'estate' | 'collaborator';
  activationStep: ActivationStep;
  activationForm: ActivationFormState | CollaboratorActivationFormState;
  updateActivationFormField: UpdateActivationFormFieldFn;
  handleZipCodeLookup: (zip: string) => void;
  addressLookupLoading: boolean;
  addressLookupError: string | null;
  addressAutoFilled: string[];
  isBrokerAdmin?: boolean;
  estateOptions?: Array<{ key: string; text: string; value: string }>;
  userOptions?: Array<{ key: string | number; text: string; value: string }>;
  usersLoading?: boolean;
  selectedEstateId?: string;
  onEstateChange?: (estateId: string) => void;
  onUserChange?: (userId: string) => void;
};

const ActivationStepContent: React.FC<Props> = ({
  mode = 'estate',
  activationStep,
  activationForm,
  updateActivationFormField,
  handleZipCodeLookup,
  addressLookupLoading,
  addressLookupError,
  addressAutoFilled,
  isBrokerAdmin = false,
  estateOptions = [],
  userOptions = [],
  usersLoading = false,
  selectedEstateId = '',
  onEstateChange,
  onUserChange
}) => {
  const collaboratorForm = activationForm as CollaboratorActivationFormState;

  if (activationStep === 1) {
    return (
      <Form>
        <Message info>
          <Message.Header>
            {mode === 'collaborator'
              ? 'Cadastro do colaborador recebedor'
              : 'Ative Cobranças para sua imobiliária'}
          </Message.Header>
          <p>
            {mode === 'collaborator'
              ? 'Este cadastro habilita o recebimento de comissões via Pagar.me.'
              : 'Este cadastro habilita o recebimento dos pagamentos das cobranças.'}
          </p>
        </Message>
        {mode === 'collaborator' && (
          <Grid>
            <Grid.Row>
              {isBrokerAdmin && (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  <Form.Field>
                    <label>Imobiliária *</label>
                    <Dropdown
                      fluid
                      search
                      selection
                      upward
                      placeholder="Selecionar..."
                      value={collaboratorForm.estateId}
                      options={estateOptions}
                      onChange={(_, { value }) => onEstateChange?.(String(value || ''))}
                    />
                  </Form.Field>
                </Grid.Column>
              )}
              <Grid.Column computer={isBrokerAdmin ? 8 : 16} tablet={16} mobile={16}>
                <Form.Field>
                  <label>Usuário vinculado (opcional)</label>
                  <Dropdown
                    fluid
                    search
                    selection
                    clearable
                    placeholder={
                      selectedEstateId ? 'Selecionar...' : 'Selecione uma imobiliária'
                    }
                    value={collaboratorForm.userId}
                    options={userOptions}
                    loading={usersLoading}
                    disabled={!selectedEstateId || usersLoading}
                    onChange={(_, { value }) => onUserChange?.(String(value || ''))}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
        <Form.Field>
          <label>Tipo de cadastro</label>
          <Dropdown
            fluid
            selection
            upward
            options={[
              { key: 'corporation', text: 'Pessoa Jurídica', value: 'corporation' },
              { key: 'individual', text: 'Pessoa Física', value: 'individual' }
            ]}
            value={activationForm.recipientType}
            onChange={(_, { value }) =>
              updateActivationFormField(
                'recipientType',
                value as ActivationFormState['recipientType']
              )
            }
          />
        </Form.Field>
      </Form>
    );
  }

  if (activationStep === 2) {
    const isCorporation = activationForm.recipientType === 'corporation';
    return (
      <Form>
        <Message info>
          <Message.Header>
            {isCorporation ? 'Dados iniciais do recebedor (Pessoa Jurídica)' : 'Dados do recebedor'}
          </Message.Header>
          <p>
            {isCorporation
              ? 'Nesta etapa informe os dados de identificação da empresa recebedora. Os dados do representante legal serão preenchidos na próxima etapa.'
              : 'Preencha os dados da pessoa recebedora das cobranças.'}
          </p>
        </Message>
        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Form.Field>
                <label>{isCorporation ? 'E-mail da empresa' : 'E-mail'}</label>
                <Input
                  value={activationForm.email}
                  onChange={(_, { value }) => updateActivationFormField('email', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>{isCorporation ? 'CNPJ da empresa' : 'CPF'}</label>
                <Input
                  value={activationForm.document}
                  onChange={(_, { value }) =>
                    updateActivationFormField(
                      'document',
                      isCorporation ? maskCpfOrCnpj(value) : maskCpf(value)
                    )
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          {!isCorporation && (
            <>
              <Grid.Row columns={2}>
                <Grid.Column>
                  <Form.Field>
                    <label>Nome completo</label>
                    <Input
                      value={activationForm.name}
                      onChange={(_, { value }) => updateActivationFormField('name', value)}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Nome da mãe (opcional)</label>
                    <Input
                      value={activationForm.motherName}
                      onChange={(_, { value }) => updateActivationFormField('motherName', value)}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={3}>
                <Grid.Column>
                  <Form.Field>
                    <label>Data de nascimento</label>
                    <Input
                      type="date"
                      value={activationForm.birthdate}
                      onChange={(_, { value }) => updateActivationFormField('birthdate', value)}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Renda mensal (R$)</label>
                    <Input
                      type="number"
                      value={activationForm.monthlyIncome}
                      onChange={(_, { value }) => updateActivationFormField('monthlyIncome', value)}
                    />
                  </Form.Field>
                </Grid.Column>
                <Grid.Column>
                  <Form.Field>
                    <label>Profissão</label>
                    <Input
                      value={activationForm.professionalOccupation}
                      onChange={(_, { value }) =>
                        updateActivationFormField('professionalOccupation', value)
                      }
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row columns={1}>
                <Grid.Column>
                  <Form.Field>
                    <label>Telefone</label>
                    <Input
                      value={activationForm.phone}
                      onChange={(_, { value }) => updateActivationFormField('phone', maskPhoneNumber(value))}
                      placeholder="(00) 00000-0000"
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>
            </>
          )}
        </Grid>
      </Form>
    );
  }

  if (activationStep === 3) {
    if (activationForm.recipientType !== 'corporation') return null;
    return (
      <Form>
        <Header as="h4" style={{ marginTop: 0 }}>
          Dados da empresa
        </Header>
        <Message info>
          <p>
            Conforme exigência de cadastro para recebedor PJ, informe abaixo os dados da empresa e do
            representante legal responsável.
          </p>
        </Message>
        <Grid>
          <Grid.Row columns={2}>
            <Grid.Column>
              <Form.Field>
                <label>E-mail da empresa</label>
                <Input
                  value={activationForm.email}
                  onChange={(_, { value }) => updateActivationFormField('email', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>CNPJ da empresa</label>
                <Input
                  value={activationForm.document}
                  onChange={(_, { value }) => updateActivationFormField('document', maskCpfOrCnpj(value))}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Telefone da empresa</label>
                <Input
                  value={activationForm.phone}
                  onChange={(_, { value }) => updateActivationFormField('phone', maskPhoneNumber(value))}
                  placeholder="(00) 00000-0000"
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Form.Field>
                <label>Razão social</label>
                <Input
                  value={activationForm.companyName}
                  onChange={(_, { value }) => updateActivationFormField('companyName', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Nome fantasia</label>
                <Input
                  value={activationForm.tradingName}
                  onChange={(_, { value }) => updateActivationFormField('tradingName', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Receita anual (R$)</label>
                <Input
                  type="number"
                  value={activationForm.annualRevenue}
                  onChange={(_, { value }) => updateActivationFormField('annualRevenue', value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Header as="h4">Responsável legal</Header>
        <Grid>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Form.Field>
                <label>Nome</label>
                <Input
                  value={activationForm.partnerName}
                  onChange={(_, { value }) => updateActivationFormField('partnerName', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>E-mail</label>
                <Input
                  value={activationForm.partnerEmail}
                  onChange={(_, { value }) => updateActivationFormField('partnerEmail', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>CPF</label>
                <Input
                  value={activationForm.partnerDocument}
                  onChange={(_, { value }) => updateActivationFormField('partnerDocument', maskCpfOrCnpj(value))}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Telefone</label>
                <Input
                  value={activationForm.partnerPhone}
                  onChange={(_, { value }) => updateActivationFormField('partnerPhone', maskPhoneNumber(value))}
                  placeholder="(00) 00000-0000"
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Form.Field>
                <label>Data de nascimento</label>
                <Input
                  type="date"
                  value={activationForm.partnerBirthdate}
                  onChange={(_, { value }) => updateActivationFormField('partnerBirthdate', value)}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Renda mensal (R$)</label>
                <Input
                  type="number"
                  value={activationForm.partnerMonthlyIncome}
                  onChange={(_, { value }) =>
                    updateActivationFormField('partnerMonthlyIncome', value)
                  }
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Profissão</label>
                <Input
                  value={activationForm.partnerProfessionalOccupation}
                  onChange={(_, { value }) =>
                    updateActivationFormField('partnerProfessionalOccupation', value)
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  }

  if (activationStep === 4) {
    return (
      <Form>
        <Grid>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Form.Field>
                <label>CEP</label>
                <Input
                  value={activationForm.zipCode}
                  onChange={(_, { value }) => {
                    const masked = maskZipCode(value);
                    updateActivationFormField('zipCode', masked);
                    if (masked.replace(/\D/g, '').length === 8) {
                      handleZipCodeLookup(masked);
                    }
                  }}
                  placeholder="00000-000"
                  loading={addressLookupLoading}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Rua</label>
                <Input
                  value={activationForm.street}
                  onChange={(_, { value }) => updateActivationFormField('street', value)}
                  disabled={addressAutoFilled.includes('street')}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Número</label>
                <Input
                  value={activationForm.streetNumber}
                  onChange={(_, { value }) => updateActivationFormField('streetNumber', value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={4}>
            <Grid.Column>
              <Form.Field>
                <label>Bairro</label>
                <Input
                  value={activationForm.neighborhood}
                  onChange={(_, { value }) => updateActivationFormField('neighborhood', value)}
                  disabled={addressAutoFilled.includes('neighborhood')}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Cidade</label>
                <Input
                  value={activationForm.city}
                  onChange={(_, { value }) => updateActivationFormField('city', value)}
                  disabled={addressAutoFilled.includes('city')}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>UF</label>
                <Input
                  maxLength={2}
                  value={activationForm.state}
                  onChange={(_, { value }) =>
                    updateActivationFormField('state', value.toUpperCase())
                  }
                  disabled={addressAutoFilled.includes('state')}
                />
              </Form.Field>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>Complemento (opcional)</label>
                <Input
                  value={activationForm.complementary}
                  onChange={(_, { value }) => updateActivationFormField('complementary', value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              {addressLookupError && <Message negative>{addressLookupError}</Message>}
              <Form.Field>
                <label>Ponto de referência</label>
                <Input
                  value={activationForm.referencePoint}
                  onChange={(_, { value }) => updateActivationFormField('referencePoint', value)}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  }

  // Step 5
  return (
    <Form>
      <div
        role="alert"
        style={{
          backgroundColor: '#fffaf3',
          border: '1px solid #c9ba9b',
          borderRadius: 4,
          color: '#573a08',
          marginBottom: 16,
          padding: '12px 16px'
        }}
      >
        <strong style={{ display: 'block', marginBottom: 4 }}>
          Conta corrente obrigatória
        </strong>
        A conta cadastrada deve ser uma conta corrente e possuir a mesma titularidade do
        documento informado no cadastro.
      </div>
      <Grid>
        <Grid.Row columns={3}>
          <Grid.Column>
            <Form.Field>
              <label>Nome do titular</label>
              <Input
                maxLength={30}
                value={activationForm.bankHolderName}
                onChange={(_, { value }) => updateActivationFormField('bankHolderName', value)}
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Tipo do titular</label>
              <Dropdown
                id="create-estate-dropdown"
                fluid
                selection
                options={[
                  { key: 'individual', text: 'Pessoa Física', value: 'individual' },
                  { key: 'company', text: 'Pessoa Jurídica', value: 'company' }
                ]}
                value={activationForm.bankHolderType}
                onChange={(_, { value }) =>
                  updateActivationFormField(
                    'bankHolderType',
                    value as ActivationFormState['bankHolderType']
                  )
                }
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Documento do titular</label>
              <Input
                value={activationForm.bankHolderDocument}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankHolderDocument', maskCpfOrCnpj(value))
                }
              />
            </Form.Field>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={5}>
          <Grid.Column>
            <Form.Field>
              <label>Código do Banco</label>
              <BankCodeDropdown
                fluid
                value={activationForm.bankCode}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankCode', String(value || ''))
                }
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Agência</label>
              <Input
                value={activationForm.bankBranchNumber}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankBranchNumber', value.replace(/\D/g, ''))
                }
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Dígito agência (opcional)</label>
              <Input
                value={activationForm.bankBranchCheckDigit}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankBranchCheckDigit', value)
                }
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Conta</label>
              <Input
                value={activationForm.bankAccountNumber}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankAccountNumber', value.replace(/\D/g, ''))
                }
              />
            </Form.Field>
          </Grid.Column>
          <Grid.Column>
            <Form.Field>
              <label>Dígito conta</label>
              <Input
                value={activationForm.bankAccountCheckDigit}
                onChange={(_, { value }) =>
                  updateActivationFormField('bankAccountCheckDigit', value)
                }
              />
            </Form.Field>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Form>
  );
};

export default ActivationStepContent;
